import { FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Paper } from "@mui/material";
import IContact, { DEFAULT_CONTACT } from "../../../models/contact";
import ContactFormFields from "../../../components/input/form/contact/ContactFormFields";
import { useTranslation } from "react-i18next";
import { EntryWrapper, FormWrapper } from "../../../components/customStyles/form";
import propertyGroupService from "../../../services/propertyGroupService/PropertyGroupService";
import { IPropertyGroup } from "../../../models/propertyGroup";
import contactService from "../../../services/contact/ContactService";
import { validEmailRegex, validPhoneRegex } from "../../../utils/regex";
import EditOnly from "../../../components/permissions/EditOnly";

const ContactEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState<boolean>(false);
  const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);
  const [cachedContact, setCachedContact] = useState<IContact | null>(null);
  const [contact, setContact] = useState<IContact>(DEFAULT_CONTACT);
  const [hasContactChanged, setHasContactChanged] = useState<boolean>(false);

  useEffect(() => {
    propertyGroupService.GetPropertyGroups().then(setPropertyGroups);
  }, []);

  useEffect(() => {
    if (state) {
      setContact(state as IContact);
      setCachedContact(state as IContact);
    }
  }, [state]);

  useEffect(() => {
    if (JSON.stringify(contact) !== JSON.stringify(cachedContact)) {
      return setHasContactChanged(true);
    }

    setHasContactChanged(false);
  }, [contact, cachedContact]);

  const onUpdateContact = async (event: FormEvent) => {
    event.preventDefault();

    try {
      await contactService.UpdatePunchContact(contact);

      setIsErrorMessageVisible(false);
      navigate("..");
    } catch (error) {
      setIsErrorMessageVisible(true);
    }
  };

  const isValidForm = () => {
    if (
      !contact.name ||
      !contact.positionFrench ||
      contact.propertyGroupIds.length <= 0 ||
      (contact.email && !validEmailRegex.test(contact.email)) ||
      (contact.phone && !validPhoneRegex.test(contact.phone))
    ) return false;

    return true;
  };

  return (
    <Box component={Paper} sx={{ width: "100%" }}>
      <form autoComplete="off" onSubmit={onUpdateContact}>
        <FormWrapper>
          {isErrorMessageVisible && (
            <Alert
              severity="error"
              sx={{ marginBottom: "1em" }}
            >
              {t("table.contact.editErrorMessage")}
            </Alert>
          )}

          <ContactFormFields
            contact={contact}
            setContact={setContact}
            propertyGroups={propertyGroups}
          />

          <EntryWrapper sx={{ justifyContent: "flex-end" }}>
            <EditOnly>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={!isValidForm() || !hasContactChanged}
                sx={{
                  margin: "1em",
                  marginLeft: "0",
                }}
              >
                {t("table.contact.edit")}
              </Button>
            </EditOnly>
          </EntryWrapper>
        </FormWrapper>
      </form>
    </Box>
  );
};

export default ContactEditPage;
